import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

const INDEX_IDS = ["SENSEX", "SP500", "NASDAQ"];

function formatNumber(value) {
  if (value == null || !Number.isFinite(Number(value))) {
    return "--";
  }
  return new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(value);
}

function formatChange(change) {
  if (change == null) {
    return { text: "N/A", color: "text.secondary" };
  }
  return {
    text: `${change.toFixed(2)}%`,
    color: change >= 0 ? "success.main" : "error.main",
  };
}

function DashboardPage() {
  const [usdInr, setUsdInr] = useState("--");
  const [indices, setIndices] = useState([]);
  const [assets, setAssets] = useState([]);
  const [status, setStatus] = useState("");

  const loadPrices = useCallback(async () => {
    setStatus("Loading latest prices...");

    try {
      const response = await fetch("/api/prices");
      const text = await response.text();
      let payload;
      try {
        payload = JSON.parse(text);
      } catch {
        const hint =
          text.trimStart().startsWith("<!DOCTYPE") || text.trimStart().startsWith("<html")
            ? "Got HTML instead of JSON — use npm start (runs the prices API on :3001), or open the deployed site on Vercel."
            : "Response was not valid JSON.";
        throw new Error(hint);
      }

      if (!response.ok) {
        const msg =
          payload?.details || payload?.error || "Unknown API error";
        throw new Error(
          typeof msg === "string" ? msg : JSON.stringify(msg)
        );
      }

      const inr = payload.fx?.usdInr;
      setUsdInr(
        inr != null && Number.isFinite(inr) ? formatNumber(inr) : "--"
      );
      const allAssets = payload.assets || [];
      setIndices(
        INDEX_IDS.map((id) => allAssets.find((asset) => asset.id === id)).filter(
          Boolean
        )
      );
      setAssets(allAssets.filter((asset) => !INDEX_IDS.includes(asset.id)));
      setStatus("");
    } catch (error) {
      setStatus(`Failed to load prices: ${error.message}`);
    }
  }, []);

  useEffect(() => {
    loadPrices();
    const id = setInterval(loadPrices, 60000);
    return () => clearInterval(id);
  }, [loadPrices]);

  const cardSx = {
    bgcolor: "var(--card-bg)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    border: "1px solid var(--card-border)",
    borderRadius: "20px",
    boxShadow: "var(--shadow-md)",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-4px)",
      borderColor: "rgba(102, 126, 234, 0.3)",
      boxShadow: "var(--shadow-lg)",
    },
  };

  return (
    <Box
      component="main"
      className="market-dashboard"
      sx={{ width: "100%", maxWidth: 1100, mx: "auto" }}
    >
      <Stack spacing={3}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
        >
          <Typography variant="h4" component="h1" sx={{ mb: 0 }}>
            Market Dashboard
          </Typography>
          <Button variant="contained" color="primary" onClick={loadPrices}>
            Refresh
          </Button>
        </Stack>

        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              lg: "repeat(4, minmax(0, 1fr))",
            },
          }}
        >
          <Paper elevation={0} sx={{ p: 2.5, ...cardSx }}>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              USD / INR
            </Typography>
            <Typography variant="h5" component="p" sx={{ fontWeight: 700 }}>
              {usdInr}
            </Typography>
          </Paper>
          {indices.map((index) => {
            const change =
              typeof index.changePercent === "number"
                ? index.changePercent
                : null;
            const { text: changeText, color: changeColor } =
              formatChange(change);

            return (
              <Paper key={index.id} elevation={0} sx={{ p: 2.5, ...cardSx }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ textTransform: "uppercase", letterSpacing: 0.06 }}
                >
                  {index.label}
                </Typography>
                <Typography variant="h5" component="p" sx={{ mt: 1, fontWeight: 700 }}>
                  {formatNumber(index.value)}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mt: 1.5, color: changeColor, fontWeight: 600 }}
                >
                  {changeText}
                </Typography>
              </Paper>
            );
          })}
        </Box>

        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              md: "repeat(3, minmax(0, 1fr))",
            },
          }}
        >
          {assets.map((asset) => {
            const change =
              typeof asset.changePercent === "number"
                ? asset.changePercent
                : null;
            const { text: changeText, color: changeColor } =
              formatChange(change);

            return (
              <Paper
                key={asset.id}
                elevation={0}
                sx={{ p: 2.5, ...cardSx }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ textTransform: "uppercase", letterSpacing: 0.06 }}
                >
                  {asset.label}
                </Typography>
                <Typography variant="h5" component="p" sx={{ mt: 1, fontWeight: 700 }}>
                  {formatNumber(asset.value)}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1.5, color: changeColor, fontWeight: 600 }}>
                  {changeText}
                </Typography>
              </Paper>
            );
          })}
        </Box>

        <Typography variant="body2" color="text.secondary">
          Auto refresh every 60 seconds.
        </Typography>
        {status ? (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              bgcolor: "rgba(255, 255, 255, 0.03)",
              border: "1px solid var(--card-border)",
              borderRadius: "16px",
              color: status.startsWith("Failed") ? "error.light" : "text.secondary",
            }}
          >
            <Typography variant="body2">{status}</Typography>
          </Paper>
        ) : null}
      </Stack>
    </Box>
  );
}

export default DashboardPage;
